import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

export default function UserEventList() {
  const { token, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch('https://techservicemarket-backend.onrender.com/api/events', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setEvents(data);
        } else {
          throw new Error('Invalid response format');
        }
      })
      .catch(err => {
        console.error('Error fetching events:', err);
        setError('Failed to load events.');
      })
      .finally(() => setIsLoading(false));
  }, [token]);

  const isRegistered = (event) =>
    event.rsvps?.some(r => r.userId === user?.id);

  const isAuthor = (event) =>
    event.author?._id === user?.id;

  const handleRegister = async (eventId) => {
    try {
      const res = await fetch(`https://techservicemarket-backend.onrender.com/api/events/${eventId}/rsvp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.message || 'Registration failed');
      setEvents(events.map(ev => ev._id === eventId ? updated : ev));
      setMessage('Registration successful');
    } catch (err) {
      console.error('Registration error:', err);
      setMessage(err.message || 'Registration failed');
    }
  };

  const handleCancelRegistration = async (eventId) => {
    try {
      const res = await fetch(`https://techservicemarket-backend.onrender.com/api/events/${eventId}/rsvp`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.message || 'Cancel failed');
      setEvents(events.map(ev => ev._id === eventId ? updated : ev));
      setMessage('Registration cancelled');
    } catch (err) {
      console.error('Cancel error:', err);
      setMessage(err.message || 'Failed to cancel registration');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      const res = await fetch(`https://techservicemarket-backend.onrender.com/api/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message);
      setEvents(events.filter(ev => ev._id !== eventId));
      setMessage("Event deleted.");
    } catch (err) {
      console.error("Delete error:", err);
      setMessage(err.message || "Delete failed.");
    }
  };

  const filteredEvents = events.filter(event => {
    if (filter === 'registered') return isRegistered(event);
    if (filter === 'notRegistered') return !isRegistered(event);
    return true;
  });

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem' }}>
      <h2 style={{ textAlign: 'center', color: '#1e40af', fontSize: '2rem', marginBottom: '1rem' }}>
        Upcoming Events
      </h2>

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <label>Filter: </label>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '4px' }}
        >
          <option value="all">All</option>
          <option value="registered">Registered</option>
          <option value="notRegistered">Not Registered</option>
        </select>
      </div>

      {message && <p style={{ color: '#047857', textAlign: 'center' }}>{message}</p>}
      {error && <p style={{ color: '#dc2626', textAlign: 'center' }}>{error}</p>}
      {isLoading && <p style={{ textAlign: 'center' }}>Loading events...</p>}

      {!isLoading && filteredEvents.length === 0 && (
        <p style={{ textAlign: 'center' }}>No events found for this filter.</p>
      )}

      {!isLoading && filteredEvents.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {filteredEvents.map(event => (
            <div
              key={event._id}
              style={{
                backgroundColor: '#f9fafb',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{ color: '#111827', marginBottom: '0.5rem' }}>{event.title}</h3>
              <p><strong>Date:</strong> {new Date(event.eventDate).toLocaleString()}</p>
              <p><strong>Location:</strong> {event.location}</p>
              <p><strong>Registered:</strong> {event.attendanceCount}</p>
              <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                Hosted by: {event.author?.username || 'Unknown'} ({event.author?.role || ''})
              </p>

              {event.rsvps?.length > 0 && (
                <div style={{ fontSize: '0.9rem', color: '#374151', marginTop: '0.5rem' }}>
                  <strong>Attendees:</strong> {event.rsvps.map(r => r.user?.username).filter(Boolean).join(', ') || 'No names'}
                </div>
              )}

              <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {isRegistered(event) ? (
                  <button onClick={() => handleCancelRegistration(event._id)} style={cancelButton}>
                    Cancel Registration
                  </button>
                ) : (
                  <button onClick={() => handleRegister(event._id)} style={registerButton}>
                    Register
                  </button>
                )}
                {isAuthor(event) && (
                  <>
                    <button style={editButton}>Edit</button>
                    <button onClick={() => handleDeleteEvent(event._id)} style={deleteButton}>
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const registerButton = {
  backgroundColor: '#1e40af',
  color: 'white',
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: '4px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const cancelButton = {
  ...registerButton,
  backgroundColor: '#6b7280'
};

const editButton = {
  ...registerButton,
  backgroundColor: '#0284c7'
};

const deleteButton = {
  ...registerButton,
  backgroundColor: '#dc2626'
};